import styles from 'css/src/components/sketch/SketchControls.module.scss';

import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import classNames from 'classnames';
import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { AVAILABLE_MODELS, Model } from 'js/src/agent/types';
import type { AIVoice, Blueprint, BrandKit, Template } from 'js/src/client';
import { NarrativeThreadEnum } from 'js/src/client';
import { MediaSourcesModal } from 'js/src/components/app/doc-to-script/MediaSourcesModal';
import TemplatePickerModal from 'js/src/components/app/template-preview/TemplatePickerModal';
import VoiceCloningModal from 'js/src/components/app/VoiceCloningModal/VoiceCloningModal';
import VoiceEditModal from 'js/src/components/app/VoiceCloningModal/VoiceEditModal';
import VoiceSelectionModal from 'js/src/components/app/VoiceSelectionModal/VoiceSelectionModal';
import {
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    TertiaryIconButton,
} from 'js/src/components/common/ui/Button';
import {
    Dropdown,
    DROPDOWN_DIVIDER,
    DROPDOWN_HEADER,
    type DropdownItemProp,
} from 'js/src/components/common/ui/Dropdown';
import { useEnsureSelectedVoiceLoaded } from 'js/src/components/sketch/hooks/useEnsureSelectedVoiceLoaded';
import { buildDurationDropdownItems } from 'js/src/components/sketch/sketch-duration-utils';
import { buildFormatDropdownItems } from 'js/src/components/sketch/sketch-format-utils';
import SketchBlueprintsModal from 'js/src/components/sketch/SketchBlueprintsModal';
import type { SketchControlsBadgesState } from 'js/src/components/sketch/SketchControlsBadges';
import {
    BADGE_SIZES,
    isMediaSourcesDefault,
    SketchControlsBadges,
} from 'js/src/components/sketch/SketchControlsBadges';
import type {
    SketchBlueprintsModalContext,
    TemplatePickerModalContext,
    VoiceSelectionModalContext,
} from 'js/src/data/modals';
import {
    hideModal,
    LOCKED_FEATURES,
    MODAL_IDS,
    showModal,
    VOICE_SELECTION_MODAL_SOURCE,
} from 'js/src/data/modals';
import { selectSketchControlsModel } from 'js/src/data/sketch-controls/sketch-controls-selectors';
import { trackUpsellClicked } from 'js/src/libs/analytics-utils';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import Flags from 'js/src/libs/flags';
import { useAppDispatch } from 'js/src/libs/RootState';

export type SketchControl =
    | 'blueprint'
    | 'template'
    | 'narrativeThread'
    | 'duration'
    | 'format'
    | 'voice'
    | 'mediaSources'
    | 'brandKit'
    | 'model';

export type SketchControlsSource = 'chat' | 'widget';

export type ClearableSketchControl = Extract<
    SketchControl,
    | 'blueprint'
    | 'template'
    | 'narrativeThread'
    | 'duration'
    | 'format'
    | 'mediaSources'
    | 'model'
    | 'brandKit'
>;

const SKETCH_CONTROL_TEXT: Record<SketchControl, string> = {
    blueprint: 'Blueprint',
    template: 'Template',
    narrativeThread: 'Voiceover',
    duration: 'Target duration',
    format: 'Format',
    voice: 'Change AI voice',
    mediaSources: 'Media settings',
    brandKit: 'Brand kit',
    model: 'Model',
};

export const SKETCH_CONTROL_ICON: Record<SketchControl, string> = {
    blueprint: 'blueprints',
    template: 'auto_awesome_mosaic',
    narrativeThread: 'article',
    duration: 'timer',
    format: 'aspect_ratio',
    voice: 'record_voice_over',
    mediaSources: 'perm_media',
    brandKit: 'palette',
    model: 'borg',
};

export function SettingsToggleButton({
    ref,
    ...props
}: {
    onClick: VoidFunction;
    disabled: boolean;
    ref?: React.Ref<HTMLButtonElement>;
}) {
    return (
        <TertiaryIconButton
            {...props}
            ref={ref}
            variant={BUTTON_VARIANTS.OUTLINE}
            size={BUTTON_SIZES.MD}
            icon="tune"
        />
    );
}

export interface SketchControlsProps {
    className?: string;
    source: SketchControlsSource;
    dropdownDisabled: boolean;
    badgesState: SketchControlsBadgesState;
    scrollable?: boolean;
    badgeSize?: BADGE_SIZES;
    dropdownTogglePosition?: 'start' | 'inline-end';
    hideDuration?: boolean;
    hideNarrativeThread?: boolean;
    hideVoice?: boolean;
    hideBlueprint?: boolean;
    selectedTemplateId: string | null;
    selectedAIVoiceId: string | null;
    canUseAIVoiceover: boolean;
    brandKits: Immutable.List<ImmutableMap<BrandKit>>;
    supportedAspectRatios: AspectRatio[] | null;
    onSelectControl: (control: SketchControl, value?: unknown) => void;
    onSelectBlueprint: (blueprint: Blueprint) => void;
    onSelectTemplate: (template: ImmutableMap<Template>) => void;
    onSelectVoice: (voice: AIVoice) => void;
    onSelectDuration: (seconds: number) => void;
    onSelectAspectRatio: (aspectRatio: AspectRatio) => void;
    onClearControl: (control: ClearableSketchControl) => void;
}

export const SketchControls = React.memo(function SketchControls({
    className,
    source,
    dropdownDisabled,
    badgesState,
    scrollable = true,
    badgeSize,
    dropdownTogglePosition = 'start',
    hideDuration = false,
    hideNarrativeThread = false,
    hideVoice = false,
    hideBlueprint = false,
    selectedTemplateId,
    selectedAIVoiceId,
    canUseAIVoiceover,
    brandKits,
    supportedAspectRatios,
    onSelectControl,
    onSelectBlueprint,
    onSelectTemplate,
    onSelectVoice,
    onSelectDuration,
    onSelectAspectRatio,
    onClearControl,
}: SketchControlsProps) {
    const dispatch = useAppDispatch();
    useEnsureSelectedVoiceLoaded(selectedAIVoiceId);

    const [isMediaSourcesModalOpen, setIsMediaSourcesModalOpen] = useState(false);

    const showDeveloperTools = Flags.chatTabDeveloperToolsEnabled();
    // The model the agent will actually use: the explicitly-selected one, or the
    // Sketch agent's default when none has been picked. Used to mark the current
    // choice in the Model submenu.
    const selectedModelValue = useSelector(selectSketchControlsModel) ?? Model.GPT_4_1_MINI;

    const getNarrativeControlItem = useCallback((canUseAIVoiceover: boolean) => {
        if (canUseAIVoiceover) {
            return {
                id: 'narrative-thread',
                nestedDropdown: {
                    toggleProps: {
                        text: 'Voiceover',
                        icon: 'article',
                    },
                    itemPropsList: [
                        {
                            id: 'voiceover',
                            text: 'Voiceover',
                            icon: 'record_voice_over',
                            onSelectData: {
                                control: 'narrativeThread' as const,
                                value: NarrativeThreadEnum.VOICEOVER,
                            },
                        },
                        {
                            id: 'no-voiceover',
                            text: 'No voiceover',
                            icon: 'voice_over_off',
                            onSelectData: {
                                control: 'narrativeThread' as const,
                                value: NarrativeThreadEnum.TEXT,
                            },
                        },
                    ],
                },
            };
        }
        return {
            id: 'voiceover-disabled',
            text: 'Voiceover',
            icon: 'record_voice_over',
            disabled: true,
            tooltipTitle: 'AI voiceover is disabled. Contact your admin to enable.',
        };
    }, []);

    const getDurationItem = useCallback(() => {
        const durationItems = buildDurationDropdownItems((seconds, isLocked) => ({
            control: 'duration' as const,
            value: isLocked ? `locked-${seconds}` : seconds,
        }));

        return {
            id: 'duration',
            nestedDropdown: {
                toggleProps: {
                    text: SKETCH_CONTROL_TEXT['duration'],
                    icon: SKETCH_CONTROL_ICON['duration'],
                },
                itemPropsList: durationItems,
                limitMenuHeight: true,
            },
        };
    }, []);

    const getFormatItem = useCallback(() => {
        const availableRatios =
            selectedTemplateId && supportedAspectRatios
                ? supportedAspectRatios
                : VideoData.ALL_ASPECT_RATIOS;
        const formatItems = buildFormatDropdownItems(
            (aspectRatio) => ({
                control: 'format' as const,
                value: aspectRatio,
            }),
            availableRatios,
            badgesState.aspectRatio,
        );

        return {
            id: 'format',
            nestedDropdown: {
                toggleProps: {
                    text: SKETCH_CONTROL_TEXT['format'],
                    icon: SKETCH_CONTROL_ICON['format'],
                },
                itemPropsList: formatItems,
            },
        };
    }, [badgesState.aspectRatio, selectedTemplateId, supportedAspectRatios]);

    const getBrandKitItem = useCallback(() => {
        const brandKitItems = [];
        brandKits.forEach((brandKit) => {
            brandKitItems.push({
                id: `brand-kit-${brandKit.get('id')}`,
                text: brandKit.get('name'),
                onSelectData: {
                    control: 'brandKit' as const,
                    value: brandKit.get('id'),
                },
            });
        });

        // If no brand kits, show "Create new brand kit" option
        if (brandKitItems.length === 0) {
            brandKitItems.push({
                id: 'create-brand-kit',
                text: 'Create new brand kit',
                icon: 'add',
                onSelectData: {
                    control: 'brandKit' as const,
                    value: -1,
                },
            });
        }

        return {
            id: 'brand-kit',
            nestedDropdown: {
                toggleProps: {
                    text: SKETCH_CONTROL_TEXT.brandKit,
                    icon: SKETCH_CONTROL_ICON.brandKit,
                },
                itemPropsList: brandKitItems,
            },
        };
    }, [brandKits]);

    type DropdownItemType = {
        id: string;
        text?: string;
        icon?: string;
        onSelectData?: { control: SketchControl; value?: unknown };
        disabled?: boolean;
        tooltipTitle?: string;
        children?: React.ReactNode;
        nestedDropdown?: {
            toggleProps: { text: string; icon?: string };
            itemPropsList: Array<{
                id: string;
                text?: string;
                icon?: string;
                onSelectData: { control: SketchControl; value?: unknown };
                children?: React.ReactNode;
            }>;
        };
    };

    const mediaSourcesBadgeVisible = Boolean(
        badgesState.mediaSources && !isMediaSourcesDefault(badgesState.mediaSources),
    );
    const narrativeBadgeVisible = Boolean(badgesState.narrativeThread);

    const settingsItemPropsList = useMemo(() => {
        const items: DropdownItemType[] = [];

        const showDuration = !hideDuration && !badgesState.durationSeconds;
        const showFormat = !badgesState.aspectRatio;
        const showBrandKit = !badgesState.brandKitName;
        const showTemplate = !badgesState.templateName;
        const showBlueprint = !hideBlueprint && !badgesState.blueprintName;
        const showNarrative = !hideNarrativeThread && !narrativeBadgeVisible;
        const showVoice = !hideVoice && !narrativeBadgeVisible;
        const showMediaSources = !mediaSourcesBadgeVisible;
        const showModel = showDeveloperTools && !badgesState.modelLabel;

        // "Select" subheader
        items.push({ id: DROPDOWN_HEADER, text: 'Select' });

        // Group 1: Duration, Format, Brand kit, Template, Blueprint
        if (showDuration) {
            items.push(getDurationItem());
        }
        if (showFormat) {
            items.push(getFormatItem());
        }
        if (showBrandKit) {
            items.push(getBrandKitItem());
        }
        if (showTemplate) {
            items.push({
                id: 'template',
                text: SKETCH_CONTROL_TEXT.template,
                icon: SKETCH_CONTROL_ICON.template,
                onSelectData: { control: 'template' },
            });
        }
        if (showBlueprint) {
            items.push({
                id: 'blueprint',
                text: SKETCH_CONTROL_TEXT.blueprint,
                icon: SKETCH_CONTROL_ICON.blueprint,
                onSelectData: { control: 'blueprint' },
            });
        }

        // Divider (only if we're going to show narrative/voice group below)
        if (showNarrative || showVoice) {
            items.push({ id: DROPDOWN_DIVIDER });
        }

        // Group 2: Voiceover, Voice
        if (showNarrative) {
            items.push(getNarrativeControlItem(canUseAIVoiceover));
        }
        if (showVoice) {
            items.push({
                id: 'voice',
                text: SKETCH_CONTROL_TEXT.voice,
                icon: SKETCH_CONTROL_ICON.voice,
                onSelectData: { control: 'voice' },
            });
        }

        // Group 3: Media settings, Model
        if (showMediaSources || showModel) {
            items.push({ id: DROPDOWN_DIVIDER });
        }
        if (showMediaSources) {
            items.push({
                id: 'mediaSources',
                text: SKETCH_CONTROL_TEXT.mediaSources,
                icon: SKETCH_CONTROL_ICON.mediaSources,
                onSelectData: { control: 'mediaSources' },
            });
        }
        if (showModel) {
            items.push({
                id: 'model',
                nestedDropdown: {
                    toggleProps: {
                        text: SKETCH_CONTROL_TEXT.model,
                        icon: SKETCH_CONTROL_ICON.model,
                    },
                    itemPropsList: AVAILABLE_MODELS.map((modelOption) => ({
                        id: modelOption.value,
                        text: modelOption.label,
                        // Mark the active model with a leading check so the
                        // current/default selection is obvious in the submenu.
                        icon: modelOption.value === selectedModelValue ? 'check' : undefined,
                        onSelectData: {
                            control: 'model' as const,
                            value: modelOption.value,
                        },
                    })),
                },
            });
        }

        return ImmutableFromJS(items) as Immutable.List<
            DropdownItemProp<{ control: SketchControl; value: string }>
        >;
    }, [
        badgesState.aspectRatio,
        badgesState.blueprintName,
        badgesState.brandKitName,
        badgesState.durationSeconds,
        badgesState.modelLabel,
        badgesState.templateName,
        canUseAIVoiceover,
        getBrandKitItem,
        getDurationItem,
        getFormatItem,
        getNarrativeControlItem,
        hideDuration,
        hideNarrativeThread,
        hideVoice,
        hideBlueprint,
        mediaSourcesBadgeVisible,
        narrativeBadgeVisible,
        selectedModelValue,
        showDeveloperTools,
    ]);

    const handleSelectVoice = useCallback(() => {
        const context: VoiceSelectionModalContext = {
            source: VOICE_SELECTION_MODAL_SOURCE.SCRIPT_EDIT_PAGE,
            initialSelectedVoiceId: selectedAIVoiceId || null,
            initialVoiceSpeed: 1,
            onApply: (voice: AIVoice) => {
                dispatch(hideModal());
                analytics.track('Sketch: Control - Voice Selected', {
                    ai_voice: voice.slug,
                    source,
                });
                onSelectVoice(voice);
            },
        };
        dispatch(
            showModal({
                modal_id: MODAL_IDS.AI_VOICE_SELECTION,
                modal_context: ImmutableFromJS(context),
            }),
        );
    }, [dispatch, onSelectVoice, selectedAIVoiceId, source]);

    const handleSelect = useCallback(
        (onSelectData: ImmutableMap<{ control: SketchControl; value: unknown }>) => {
            const control = onSelectData.get('control');
            analytics.track('Sketch: Control Clicked', { action: control, source });

            switch (control) {
                case 'duration': {
                    const value = onSelectData.get('value');
                    if (typeof value === 'string' && String(value).startsWith('locked-')) {
                        trackUpsellClicked('duration_dropdown_locked_option');
                        dispatch(
                            showModal({
                                modal_id: MODAL_IDS.FEATURE_LOCK,
                                modal_context: ImmutableFromJS({
                                    featureName: LOCKED_FEATURES.MAX_VIDEO_DURATION_LIMIT,
                                }),
                            }),
                        );
                    } else {
                        const seconds = Number(value);
                        if (seconds > 0) {
                            analytics.track('Sketch: Control - Duration Selected', {
                                duration_seconds: seconds,
                                source,
                            });
                            onSelectDuration(seconds);
                        }
                    }
                    break;
                }
                case 'format': {
                    const value = onSelectData.get('value') as AspectRatio;
                    if (value) {
                        analytics.track('Sketch: Control - Aspect Ratio Selected', {
                            aspect_ratio: value,
                            source,
                        });
                        onSelectAspectRatio(value);
                    }
                    break;
                }
                case 'mediaSources':
                    setIsMediaSourcesModalOpen(true);
                    break;
                case 'blueprint': {
                    const context: SketchBlueprintsModalContext = {
                        onSelect: (blueprint: Blueprint) => {
                            dispatch(hideModal());
                            analytics.track('Sketch: Control - Blueprint Selected', {
                                blueprint_id: blueprint.id,
                                blueprint_name: blueprint.name,
                                narrative_thread: blueprint.narrative_thread,
                                template_id: blueprint.default_template_id,
                                source,
                            });
                            onSelectBlueprint(blueprint);
                        },
                    };
                    dispatch(
                        showModal({
                            modal_id: MODAL_IDS.SKETCH_BLUEPRINTS,
                            modal_context: ImmutableFromJS(context),
                        }),
                    );
                    break;
                }
                case 'template': {
                    const context: TemplatePickerModalContext = {
                        onSelect: (template: ImmutableMap<Template>) => {
                            dispatch(hideModal());
                            analytics.track('Sketch: Control - Template Selected', {
                                template_id: template.get('id'),
                                template_name: template.get('display_name'),
                                source,
                            });
                            onSelectTemplate(template);
                        },
                    };
                    dispatch(
                        showModal({
                            modal_id: MODAL_IDS.TEMPLATE_PICKER,
                            modal_context: ImmutableFromJS(context),
                        }),
                    );
                    break;
                }
                case 'voice': {
                    handleSelectVoice();
                    break;
                }
                case 'narrativeThread': {
                    const value = onSelectData.get('value');
                    analytics.track('Sketch: Control - Narrative Thread Selected', {
                        narrative_thread: value,
                        source,
                    });
                    onSelectControl(control, value);
                    break;
                }
                case 'brandKit': {
                    const value = onSelectData.get('value');
                    analytics.track('Sketch: Control - Brand Kit Selected', {
                        brand_kit_id: value,
                        source,
                    });
                    onSelectControl(control, value);
                    break;
                }
                case 'model': {
                    const value = onSelectData.get('value');
                    analytics.track('Sketch: Control - Model Selected', {
                        model: value,
                        source,
                    });
                    onSelectControl(control, value);
                    break;
                }
                default: {
                    const _exhaustive: never = control;
                    throw new Error(`Unhandled control: ${_exhaustive}`);
                }
            }
        },
        [
            dispatch,
            handleSelectVoice,
            onSelectAspectRatio,
            onSelectBlueprint,
            onSelectControl,
            onSelectDuration,
            onSelectTemplate,
            source,
        ],
    );

    const handleEditBadge = useCallback(
        (control: SketchControl) => {
            handleSelect(
                ImmutableFromJS({ control }) as ImmutableMap<{
                    control: SketchControl;
                    value: unknown;
                }>,
            );
        },
        [handleSelect],
    );

    const handleBadgeDurationSelect = useCallback(
        (seconds: number) => {
            analytics.track('Sketch: Control - Duration Selected', {
                duration_seconds: seconds,
                source,
            });
            onSelectDuration(seconds);
        },
        [onSelectDuration, source],
    );

    const handleBadgeFormatSelect = useCallback(
        (aspectRatio: AspectRatio) => {
            analytics.track('Sketch: Control - Aspect Ratio Selected', {
                aspect_ratio: aspectRatio,
                source,
            });
            onSelectAspectRatio(aspectRatio);
        },
        [onSelectAspectRatio, source],
    );

    const handleBadgeBrandKitSelect = useCallback(
        (brandKitId: number) => {
            analytics.track('Sketch: Control - Brand Kit Selected', {
                brand_kit_id: brandKitId,
                source,
            });
            onSelectControl('brandKit', brandKitId);
        },
        [onSelectControl, source],
    );

    const handleClearControl = useCallback(
        (control: ClearableSketchControl) => {
            analytics.track('Sketch: Control Cleared', { action: control, source });
            onClearControl(control);
        },
        [onClearControl, source],
    );

    const dropdowns = useMemo(() => {
        const hasSettingsDropdown = settingsItemPropsList.size > 0;
        if (!hasSettingsDropdown) return null;

        return (
            <Dropdown
                breakOverflow
                ToggleComponent={SettingsToggleButton}
                toggleProps={Immutable.Map({ disabled: dropdownDisabled })}
                itemPropsList={settingsItemPropsList}
                onSelect={handleSelect}
            />
        );
    }, [dropdownDisabled, handleSelect, settingsItemPropsList]);

    return (
        <>
            <div className={classNames(styles['sketch-controls'], className)}>
                {dropdownTogglePosition === 'start' && dropdowns}

                <div
                    className={classNames('d-flex align-items-center', {
                        'overflow-x-auto overflow-y-hidden text-nowrap flex-nowrap flex-fill':
                            scrollable,
                        'flex-wrap': !scrollable,
                        'gap-1': badgeSize === BADGE_SIZES.SM,
                        'gap-2': !badgeSize || badgeSize === BADGE_SIZES.MD,
                    })}
                >
                    <SketchControlsBadges
                        state={badgesState}
                        size={badgeSize}
                        disabled={dropdownDisabled}
                        hideDuration={hideDuration}
                        supportedAspectRatios={
                            selectedTemplateId && supportedAspectRatios
                                ? supportedAspectRatios
                                : undefined
                        }
                        brandKits={brandKits}
                        onClear={handleClearControl}
                        onEdit={handleEditBadge}
                        onSelectDuration={handleBadgeDurationSelect}
                        onSelectFormat={handleBadgeFormatSelect}
                        onSelectBrandKit={handleBadgeBrandKitSelect}
                    />
                    {dropdownTogglePosition === 'inline-end' && dropdowns}
                </div>
            </div>

            <MediaSourcesModal
                isOpen={isMediaSourcesModalOpen}
                onClose={() => setIsMediaSourcesModalOpen(false)}
            />
            <VoiceSelectionModal />
            <VoiceCloningModal />
            <VoiceEditModal />
            <TemplatePickerModal />
            <SketchBlueprintsModal />
        </>
    );
});
